import { Toaster } from "@/components/ui/toaster";
import { applyPageMeta, STATIC_META } from "@/lib/seo";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import MentionsLegales from "./pages/MentionsLegales";
import PolitiqueConfidentialite from "./pages/PolitiqueConfidentialite";
import CGV from "./pages/CGV";
import Blog from "./pages/Blog";
import BlogArticle from "./pages/BlogArticle";
import SocialNetworks from "./pages/SocialNetworks";
import CreateProduct from "./pages/CreateProduct";
import Commandes from "./pages/Commandes";
import Avantages from "./pages/Avantages";
import ProjectDetails from "./pages/ProjectDetails";
import FAQ from "./pages/FAQ";
import QuiSommesNous from "./pages/QuiSommesNous";
import IndexEditor from "./pages/IndexEditor";
import IndexEditorMobile from "./pages/IndexEditorMobile";
import Cart from "./pages/Cart";
import AlbumPhotos from "./pages/AlbumPhotos";
import SignUp from "./pages/SignUp";
import SignIn from "./pages/SignIn";
import Confirm from "./pages/Confirm";
import PasswordNew from "./pages/PasswordNew";
import PasswordEdit from "./pages/PasswordEdit";
import OccasionPage, { OCCASION_PAGES } from "./pages/OccasionPage";
import ProgrammeFidelite from "./pages/ProgrammeFidelite";

const queryClient = new QueryClient();

const ScrollToAnchor = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      // Sans hash : on repart du haut de la page à chaque navigation
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, [location]);

  return null;
};

const RouteMeta = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const m = STATIC_META[pathname.replace(/\/$/, "") || "/"];
    applyPageMeta({ title: m?.title, description: m?.description, image: m?.image, path: pathname });
  }, [pathname]);
  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToAnchor />
        <RouteMeta />
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/mentions_legales" element={<MentionsLegales />} />
          <Route path="/politique_confidentialite" element={<PolitiqueConfidentialite />} />
          <Route path="/cgv" element={<CGV />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/social_networks" element={<SocialNetworks />} />
          <Route path="/social_networks/:id/albums" element={<AlbumPhotos />} />
          <Route path="/create_book" element={<CreateProduct />} />
          <Route path="/commandes" element={<Commandes />} />
          <Route path="/project/:id" element={<ProjectDetails />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/qui_sommes_nous" element={<QuiSommesNous />} />
          <Route path="/social_networks/:id/editor" element={<IndexEditor />} />
          <Route path="/social_networks/:id/editor/mobile" element={<IndexEditorMobile />} />
          <Route path="/profiles/new" element={<Commandes />} />
          <Route path="/profiles/commandes" element={<Commandes />} />
          <Route path="/profiles/credit" element={<Commandes />} />
          <Route path="/profiles/avantages" element={<Avantages />} />
          <Route path="/carts" element={<Cart />} />
          {OCCASION_PAGES.map((o) => (<Route key={o.slug} path={`/${o.slug}`} element={<OccasionPage slug={o.slug} />} />))}
          <Route path="/users/sign_up" element={<SignUp />} />
          <Route path="/users/sign_in" element={<SignIn />} />
          <Route path="/confirm" element={<Confirm />} />
          <Route path="/users/password/new" element={<PasswordNew />} />
          <Route path="/users/password/edit" element={<PasswordEdit />} />
          <Route path="/programme-fidelite" element={<ProgrammeFidelite />} />
          
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
